import {
  ArrowLeft,
  Bell,
  BookOpen,
  CheckCheck,
  ExternalLink,
  Home,
  LogOut,
  MessagesSquare,
  Settings,
  ShieldCheck,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Logomark } from '@/components/layout/public-nav';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/hooks/use-auth';
import { useNotifications, type Notification } from '@/hooks/use-notifications';
import { cn } from '@/lib/utils';

const nav = [
  { to: '/dashboard',  label: 'Dashboard',  icon: Home },
  { to: '/my-courses', label: 'My Courses',  icon: BookOpen },
  { to: '/browse',     label: 'Catalog',     icon: Sparkles },
  { to: '/sisterhood', label: 'Sisterhood',  icon: Users },
  { to: '/chat',       label: 'Messages',    icon: MessagesSquare },
];

const accountNav = [
  { to: '/my-account', label: 'Account & billing', icon: Settings },
];

// Pages that are "root" — no back button shown
const ROOT_PATHS = [
  '/dashboard',
  '/my-courses',
  '/browse',
  '/sisterhood',
  '/chat',
  '/my-account',
];

// Human-readable page titles derived from pathname
function usePageMeta() {
  const location = useLocation();
  const { pathname } = location;

  if (pathname.startsWith('/course/'))         return { title: 'Course',           showBack: true };
  if (pathname.startsWith('/course-info/'))    return { title: 'Course details',   showBack: true };
  if (pathname.startsWith('/bonus-video/'))    return { title: 'Bonus lesson',     showBack: true };
  if (pathname.startsWith('/sisterhood/post/'))return { title: 'Post',             showBack: true };
  if (pathname.startsWith('/sisterhood/tag/')) return { title: 'Topic',            showBack: true };
  if (pathname === '/my-account')             return { title: 'Account & billing', showBack: false };
  if (pathname === '/my-courses')             return { title: 'My courses',        showBack: false };
  if (pathname === '/browse')                 return { title: 'Catalog',           showBack: false };
  if (pathname === '/dashboard')              return { title: 'Dashboard',         showBack: false };
  if (pathname === '/sisterhood')             return { title: 'Sisterhood',        showBack: false };
  if (pathname === '/chat')                   return { title: 'Messages',          showBack: false };

  // Fallback — show back button for any unknown sub-path
  const isRoot = ROOT_PATHS.some((p) => pathname === p);
  return { title: '', showBack: !isRoot };
}

export function AuthedLayout() {
  const { profile, isAdmin, signOut } = useAuth();
  const navigate = useNavigate();
  const { title, showBack } = usePageMeta();

  // Called ONCE here — both bells receive data as props to avoid
  // two Supabase Realtime subscriptions to the same channel
  const notifs = useNotifications();

  return (
    <div className="flex min-h-screen bg-cream-50">
      {/* Sidebar — desktop only */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-ink/10 bg-background md:flex">
        <div className="flex h-20 items-center border-b border-ink/10 px-6">
          <Link to="/dashboard" className="flex items-center gap-3">
            <Logomark className="h-8 w-8 text-primary" />
            <div className="font-display text-base leading-none">
              Gracefully
              <span className="block text-[9px] uppercase tracking-[0.28em] text-muted-foreground">
                Rebuilt
              </span>
            </div>
          </Link>
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-6">
          <div className="eyebrow px-3 pb-2">Your space</div>
          <ul className="space-y-1">
            {nav.map((item) => (
              <SidebarLink key={item.to} {...item} />
            ))}
          </ul>

          <div className="eyebrow mt-8 px-3 pb-2">Account</div>
          <ul className="space-y-1">
            {accountNav.map((item) => (
              <SidebarLink key={item.to} {...item} />
            ))}
          </ul>

          {isAdmin && (
            <>
              <div className="eyebrow mt-8 px-3 pb-2 text-terracotta-600">Admin</div>
              <ul className="space-y-1">
                <SidebarLink to="/admin/dashboard" label="Admin portal" icon={ShieldCheck} />
              </ul>
            </>
          )}
        </nav>

        <div className="border-t border-ink/10 p-4">
          <NotificationBell {...notifs} />
          <div className="mt-2 flex items-center gap-3 rounded-lg p-2">
            <div
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-terracotta-400 to-terracotta-600 font-display text-sm font-semibold text-white"
              aria-hidden
            >
              {(profile?.display_name ?? 'G').charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{profile?.display_name ?? 'Sister'}</div>
              <div className="truncate text-xs text-muted-foreground capitalize">
                {profile?.role ?? 'member'}
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={signOut}
              aria-label="Sign out"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main column */}
      <div className="flex flex-1 flex-col overflow-x-hidden">

        {/* Top bar — always on mobile; on desktop only for sub-pages */}
        <header className={cn(
          'sticky top-0 z-30 flex h-14 shrink-0 items-center gap-3 border-b border-ink/10 bg-background/95 px-4 backdrop-blur-sm',
          showBack ? 'flex' : 'flex md:hidden',
        )}>
          {showBack ? (
            <button
              type="button"
              onClick={() => navigate(-1)}
              aria-label="Go back"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-ink/10 bg-card text-foreground/70 transition-all hover:bg-cream-100 hover:text-foreground active:scale-95"
            >
              <ArrowLeft className="h-4 w-4" />
            </button>
          ) : (
            <Link to="/dashboard" className="flex items-center gap-2 md:hidden">
              <Logomark className="h-6 w-6 text-primary" />
              <span className="font-display text-sm">Gracefully Rebuilt</span>
            </Link>
          )}

          {title && (
            <span className="flex-1 truncate font-display text-lg leading-none">
              {title}
            </span>
          )}

          {/* Notification bell — mobile only (desktop has it in sidebar) */}
          <div className="ml-auto md:hidden">
            <MobileNotificationBell {...notifs} />
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 pb-20 md:pb-0">
          <Outlet />
        </main>

        {/* Bottom nav — mobile only */}
        <MobileBottomNav />
      </div>
    </div>
  );
}

function SidebarLink({
  to,
  label,
  icon: Icon,
}: {
  to: string;
  label: string;
  icon: typeof Home;
}) {
  return (
    <li>
      <NavLink
        to={to}
        end={to === '/dashboard'}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
            isActive
              ? 'bg-terracotta-100 text-terracotta-800'
              : 'text-foreground/70 hover:bg-cream-100 hover:text-foreground',
          )
        }
      >
        <Icon className="h-4 w-4" strokeWidth={1.75} />
        {label}
      </NavLink>
    </li>
  );
}

/* ----------------------------------------------------------------
 * Notification bell — lives in sidebar, receives data as props
 * ---------------------------------------------------------------- */
type NotifProps = ReturnType<typeof useNotifications>;

function NotificationBell({ notifications, unreadCount, markOne, markAllRead }: NotifProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="relative mb-1 px-2">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ''}`}
        className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-foreground/70 transition-colors hover:bg-cream-100 hover:text-foreground"
      >
        <span className="relative">
          <Bell className="h-4 w-4" strokeWidth={1.75} />
          {unreadCount > 0 && (
            <span className="absolute -right-1.5 -top-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-terracotta-500 text-[9px] font-bold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </span>
        Notifications
      </button>

      {open && (
        <>
          {/* Click-outside dismiss */}
          <div
            className="fixed inset-0 z-40"
            aria-hidden
            onClick={() => setOpen(false)}
          />
          <div className="absolute bottom-full left-0 right-0 z-50 mb-2 overflow-hidden rounded-xl border border-ink/10 bg-card shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-ink/10 px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead(undefined)}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
            </div>

            {/* List */}
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  You're all caught up 🤍
                </p>
              ) : (
                notifications.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onRead={() => {
                      markOne(n.id);
                      if (n.link) {
                        navigate(n.link);
                        setOpen(false);
                      }
                    }}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function NotificationRow({
  notification: n,
  onRead,
}: {
  notification: Notification;
  onRead: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onRead}
      className={cn(
        'flex w-full items-start gap-3 border-b border-ink/5 px-4 py-3 text-left text-sm transition-colors last:border-0 hover:bg-cream-50',
        !n.read_at && 'bg-terracotta-50/70',
      )}
    >
      <span
        className={cn(
          'mt-1.5 h-2 w-2 shrink-0 rounded-full',
          n.read_at ? 'bg-transparent' : 'bg-terracotta-500',
        )}
      />
      <div className="min-w-0 flex-1">
        <p className="leading-snug font-medium">{n.title}</p>
        {n.body && (
          <p className="mt-0.5 truncate text-xs text-muted-foreground">{n.body}</p>
        )}
      </div>
      {n.link && (
        <ExternalLink className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground/40" />
      )}
    </button>
  );
}

/* ----------------------------------------------------------------
 * Mobile bottom navigation bar
 * ---------------------------------------------------------------- */
function MobileBottomNav() {
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-ink/10 bg-background/95 backdrop-blur-sm md:hidden">
      {nav.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          end={to === '/dashboard'}
          className={({ isActive }) =>
            cn(
              'flex flex-1 flex-col items-center gap-1 py-3 text-[10px] font-medium transition-colors',
              isActive
                ? 'text-terracotta-600'
                : 'text-foreground/50 hover:text-foreground',
            )
          }
        >
          {({ isActive }) => (
            <>
              <Icon
                className={cn('h-5 w-5', isActive && 'fill-terracotta-100')}
                strokeWidth={isActive ? 2.5 : 1.75}
              />
              {label}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/* ----------------------------------------------------------------
 * Mobile notification bell (compact, for the top bar)
 * ---------------------------------------------------------------- */
function MobileNotificationBell({ notifications, unreadCount, markOne, markAllRead }: NotifProps) {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ''}`}
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-ink/10 bg-card text-foreground/70 transition-colors hover:bg-cream-100"
      >
        <Bell className="h-4 w-4" strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-terracotta-500 text-[9px] font-bold text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" aria-hidden onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-full z-50 mt-2 w-80 overflow-hidden rounded-xl border border-ink/10 bg-card shadow-2xl">
            <div className="flex items-center justify-between border-b border-ink/10 px-4 py-3">
              <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Notifications
              </span>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllRead(undefined)}
                  className="flex items-center gap-1 text-xs text-primary hover:underline"
                >
                  <CheckCheck className="h-3 w-3" />
                  Mark all read
                </button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto">
              {notifications.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">
                  You're all caught up 🤍
                </p>
              ) : (
                notifications.map((n) => (
                  <NotificationRow
                    key={n.id}
                    notification={n}
                    onRead={() => {
                      markOne(n.id);
                      if (n.link) { navigate(n.link); setOpen(false); }
                    }}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
